import { useCallback, useEffect, useMemo, useState } from 'react';
import useHttp from '../../../hooks/use-http';
import { currencyFormatter } from '../../../functions/formatters';
import i18n from '../../../i18n';
import Icon from '../../../icons/Icon';
import QuantityStepper from '../../../components/QuantityStepper/QuantityStepper';

const CART_API = '/rest/cart/current/';
const CART_LINE_BASE = '/rest/cartline';

const CART_LINE_IMAGE_SIZE = 96;
const CART_LINE_IMAGE_SIZE_DRAWER = 72;

const PERIOD_LABELS = {
    price_per_month: 'month',
    price_per_year: 'year',
    price_per_day: 'day',
    price_per_hour: 'hour',
};

const money = (value) => {
    const amount = parseFloat(value ?? 0);
    return Number.isFinite(amount) ? amount : 0;
};

const priceUnitKey = (source) => {
    const pricing = source?.calc_pricing_type || source?.pricing_type || '';
    if (pricing && pricing !== 'price') return pricing;
    const unit = source?.unit || '';
    if (unit) return `unit:${unit}`;
    const label = source?.unit_display || '';
    if (label) return `label:${label}`;
    return 'unit:u';
};

const priceUnitLabel = (source) => {
    const pricing = source?.calc_pricing_type || source?.pricing_type || '';
    if (PERIOD_LABELS[pricing]) return i18n.t(PERIOD_LABELS[pricing]);
    return source?.unit_display || i18n.t('unit');
};

const lineProduct = (line) => line?.product_listing?.product_object || line?.product_object || {};

const childName = (line) => (
    line?.product_object?.name
    || line?.product_object?.internal_name
    || line?.name
    || ''
);

const linePriceGroups = (line, quantity, optionChildren) => {
    const groups = [];
    const addGroup = (source, amount, total) => {
        if (!amount) return;
        const key = priceUnitKey(source);
        const existing = groups.find((row) => row.key === key);
        if (existing) {
            existing.amount += amount;
            if (total) existing.total = true;
            return;
        }
        groups.push({
            key,
            label: priceUnitLabel(source),
            amount,
            total: Boolean(total),
        });
    };
    addGroup(lineProduct(line), money(line.disc_price_in_vat ?? line.price_in_vat) * quantity, true);
    optionChildren.forEach((child) => {
        addGroup(
            child.product_object || {},
            money(child.disc_price_in_vat ?? child.price_in_vat) * (child.quantity || 0),
        );
    });
    return groups;
};

const PriceGroups = ({ groups, unitPrice, unitDiscountPrice, hasDiscount }) => {
    if (groups.length <= 1) {
        const group = groups[0];
        const amount = group ? group.amount : unitDiscountPrice;
        return (
            <>
                {hasDiscount ? (
                    <>
                        <span className="text-decoration-line-through me-2">{currencyFormatter(unitPrice)}</span>
                        <span className="fw-semibold">{currencyFormatter(amount)}</span>
                    </>
                ) : (
                    <span>{currencyFormatter(amount)}</span>
                )}
                {group?.key?.startsWith('price_per_') && (
                    <span> {i18n.t('per')} {group.label}</span>
                )}
            </>
        );
    }
    return (
        <ul className="list-unstyled mb-0">
            {groups.map((row) => (
                <li key={row.key}>
                    <span className="fw-semibold">{currencyFormatter(row.amount)}</span>
                    {(!row.total || row.key.startsWith('price_per_')) && (
                        <span> {i18n.t('per')} {row.label}</span>
                    )}
                </li>
            ))}
        </ul>
    );
};

const normalizeCartPayload = (data) => {
    if (!data) return null;
    return data.serializer || data;
};

const cleanErrorMessage = (error) => {
    let text = String(error || '').trim();
    for (const prefix of ['NB: ', 'NB:', 'Let op: ', 'Let op:']) {
        if (text.startsWith(prefix)) {
            text = text.slice(prefix.length).trimStart();
            break;
        }
    }
    return text;
};

const normalizeErrorList = (errors) => {
    if (!errors) return [];
    if (Array.isArray(errors)) {
        return errors.map(cleanErrorMessage).filter(Boolean);
    }
    if (typeof errors === 'object') {
        return Object.values(errors).map(cleanErrorMessage).filter(Boolean);
    }
    if (errors === true) {
        return [i18n.t('One or more lines have errors (for example stock). Resolve them or adjust your order.')];
    }
    return [cleanErrorMessage(errors)].filter(Boolean);
};

const CartOverview = ({ initialCart = null, variant = 'page', onClose = null }) => {
    const normalizedInitialCart = useMemo(() => normalizeCartPayload(initialCart), [initialCart]);
    const [cart, setCart] = useState(normalizedInitialCart);
    const [isOverlayUpdating, setIsOverlayUpdating] = useState(false);
    const isDrawer = variant === 'drawer';

    const handleCartLoaded = useCallback((data) => {
        setCart(normalizeCartPayload(data));
    }, []);

    const { isLoading, error, sendRequest: fetchCart } = useHttp(handleCartLoaded);

    const loadCart = useCallback(() => {
        fetchCart({ url: CART_API, json: true, cancelPrevious: true });
    }, [fetchCart]);

    const handleCartLineUpdated = useCallback(() => {
        loadCart();
    }, [loadCart]);

    const { isLoading: lineLoading, error: lineError, sendRequest: cartLineUpdate } = useHttp(handleCartLineUpdated);
    const containerClassName = isDrawer ? 'container-fluid py-3 px-3 h-100 overflow-auto' : 'container py-4';

    useEffect(() => {
        setCart(normalizedInitialCart);
    }, [normalizedInitialCart]);

    useEffect(() => {
        // Always refresh from API so stock/MOQ errors stay current.
        loadCart();
    }, [loadCart]);

    const updateQuantity = useCallback(async (lineId, newQuantity) => {
        const safeQty = Math.max(0, Number.isFinite(newQuantity) ? newQuantity : 0);
        setIsOverlayUpdating(true);
        const formData = new FormData();
        formData.append('quantity', safeQty);
        cartLineUpdate({ method: 'POST', url: `${CART_LINE_BASE}/${lineId}/alter_quantity/`, json: true, data: formData });
    }, [cartLineUpdate]);

    useEffect(() => {
        if (!lineLoading && !isLoading) {
            const t = setTimeout(() => setIsOverlayUpdating(false), 250);
            return () => clearTimeout(t);
        }
    }, [lineLoading, isLoading]);

    const getPrimaryProductImage = (productObj) => {
        const imgs = productObj?.productimages_objects || [];
        if (!Array.isArray(imgs) || imgs.length === 0) return null;

        const first = [...imgs].sort((a, b) => (a?.sequence ?? 0) - (b?.sequence ?? 0))[0];
        return {
            src: first?.optimized_image || first?.image_webp || first?.image?.image_optimized || first?.image?.image || null,
            alt: first?.image?.alt_text || productObj?.name || i18n.t('Product'),
        };
    };

    const CartOverviewSkeleton = () => {
        const SkeletonLine = () => (
            <div className="row border-bottom py-3 align-items-center">
                <div className="col-12 col-md-6">
                    <div className="placeholder-glow">
                        <div className="placeholder rounded-pill col-9 mb-2"></div>
                        <div className="placeholder rounded-pill col-6"></div>
                    </div>
                </div>
                <div className="col-6 col-md-2">
                    <div className="placeholder-glow d-flex">
                        <span className="placeholder rounded-pill col-12" style={{ height: 32 }}></span>
                    </div>
                </div>
                <div className="col-4 col-md-2 text-end">
                    <div className="placeholder-glow">
                        <span className="placeholder rounded-pill col-8"></span>
                    </div>
                </div>
                <div className="col-2 col-md-2 text-end">
                    <div className="placeholder-glow d-flex justify-content-end">
                        <span className="placeholder rounded-pill col-4" style={{ height: 20 }}></span>
                    </div>
                </div>
            </div>
        );

        return (
            <div className="row g-4">
                <div className="col-12 col-lg-8">
                    <div className="card">
                        <div className="card-header bg-white d-flex align-items-center justify-content-between">
                            <div className="text-muted small text-uppercase fw-bold mb-0">{i18n.t('Items')}</div>
                            <div className="text-muted small d-flex align-items-center">
                                <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
                                <span>{i18n.t('Loading')}</span>
                            </div>
                        </div>
                        <div className="card-body py-0">
                            <SkeletonLine />
                            <SkeletonLine />
                            <SkeletonLine />
                        </div>
                    </div>
                </div>
                <div className="col-12 col-lg-4">
                    <div className="card">
                        <div className="card-header bg-white">
                            <div className="text-muted small text-uppercase fw-bold mb-0">{i18n.t('Summary')}</div>
                        </div>
                        <div className="card-body">
                            <div className="placeholder-glow">
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="placeholder rounded-pill col-4"></span>
                                    <span className="placeholder rounded-pill col-3"></span>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="placeholder rounded-pill col-3"></span>
                                    <span className="placeholder rounded-pill col-2"></span>
                                </div>
                                <hr />
                                <div className="d-flex justify-content-between align-items-center">
                                    <span className="placeholder rounded-pill col-3"></span>
                                    <span className="placeholder rounded-pill col-4"></span>
                                </div>
                                <div className="placeholder rounded-pill col-10 mt-3"></div>
                            </div>
                        </div>
                        <div className="card-footer bg-white d-grid gap-2">
                            <button type="button" className="btn btn-primary" disabled>
                                {i18n.t('Loading')}
                            </button>
                            <button type="button" className="btn btn-outline-secondary" disabled>
                                {i18n.t('Continue shopping')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const CartLineRow = ({ line }) => {
        const productObj = line.product_listing?.product_object || line.product_object || {};
        const productUrl = String(productObj.url || line.product_listing?.url || '').trim();
        const hasProductUrl = Boolean(productUrl);
        const primaryImage = getPrimaryProductImage(productObj);
        const quantity = line.quantity || 1;
        const childLines = line.child_lines || line.cart_lines || [];
        const componentChildren = childLines.filter((cl) => cl.line_role === 'component');
        const optionChildren = childLines.filter((cl) => cl.line_role !== 'component');
        const lineErrors = normalizeErrorList(line.errors);
        const [stepperQty, setStepperQty] = useState(quantity);
        const unitPrice = money(line.price_in_vat);
        const unitDiscountPrice = money(line.disc_price_in_vat ?? line.price_in_vat);
        const hasDiscount = unitDiscountPrice !== unitPrice;
        const priceGroups = linePriceGroups(line, quantity, optionChildren);
        const lineTotal = priceGroups.reduce((sum, row) => sum + row.amount, 0) || quantity * unitDiscountPrice;
        const imageSize = isDrawer ? CART_LINE_IMAGE_SIZE_DRAWER : CART_LINE_IMAGE_SIZE;

        useEffect(() => {
            setStepperQty(quantity);
        }, [quantity]);

        useEffect(() => {
            const t = setTimeout(() => {
                if (stepperQty !== quantity) {
                    updateQuantity(line.id, stepperQty);
                }
            }, 400);
            return () => clearTimeout(t);
        }, [stepperQty, quantity, line.id, updateQuantity]);

        const lineErrorList = lineErrors.length > 0 ? (
            <div className="mt-1">
                <ul className="text-danger mb-1 ps-3" style={{ fontSize: '0.8rem', listStylePosition: 'outside' }}>
                    {lineErrors.map((msg, idx) => (
                        <li key={`${line.id}-err-${idx}`}>{msg}</li>
                    ))}
                </ul>
                <div className="small text-muted">
                    {i18n.t('Remove this product from your cart to continue.')}
                </div>
            </div>
        ) : null;

        if (isDrawer) {
            return (
                <div className="py-3 border-bottom" key={line.id}>
                    <div className="d-flex align-items-start gap-3">
                        {primaryImage?.src ? (
                            hasProductUrl ? (
                                <a href={productUrl} className="d-inline-block flex-shrink-0">
                                    <img
                                        src={primaryImage.src}
                                        alt={primaryImage.alt}
                                        width={imageSize}
                                        height={imageSize}
                                        loading="lazy"
                                        className="rounded border bg-white"
                                        style={{ objectFit: 'contain' }}
                                    />
                                </a>
                            ) : (
                                <img
                                    src={primaryImage.src}
                                    alt={primaryImage.alt}
                                    width={imageSize}
                                    height={imageSize}
                                    loading="lazy"
                                    className="rounded border bg-white flex-shrink-0"
                                    style={{ objectFit: 'contain' }}
                                />
                            )
                        ) : (
                            <div
                                className="rounded border bg-light flex-shrink-0"
                                style={{ width: imageSize, height: imageSize }}
                                aria-hidden="true"
                            />
                        )}
                        <div className="flex-grow-1 min-w-0">
                            <div className="d-flex align-items-start justify-content-between gap-2">
                                <div className="min-w-0">
                                    {hasProductUrl ? (
                                        <a href={productUrl} className="text-decoration-none text-dark fw-semibold d-inline-block">
                                            {productObj.name || i18n.t('Product')}
                                        </a>
                                    ) : (
                                        <div className="text-dark fw-semibold">
                                            {productObj.name || i18n.t('Product')}
                                        </div>
                                    )}
                                    {lineErrorList}
                                    <div className="small text-muted mt-1">
                                        <PriceGroups
                                            groups={priceGroups}
                                            unitPrice={unitPrice * quantity}
                                            unitDiscountPrice={unitDiscountPrice * quantity}
                                            hasDiscount={hasDiscount}
                                        />
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-link text-muted p-0 flex-shrink-0"
                                    onClick={() => updateQuantity(line.id, 0)}
                                    disabled={lineLoading}
                                    title={i18n.t('Remove')}
                                    aria-label={i18n.t('Remove')}
                                >
                                    <Icon name="ICON_TRASH" />
                                </button>
                            </div>
                            {(componentChildren.length > 0 || optionChildren.length > 0) && (
                                <div className="small text-muted mt-2">
                                    {componentChildren.length > 0 && (
                                        <div className="mb-1">
                                            <span className="fw-semibold">{i18n.t('Includes')}: </span>
                                            {componentChildren.map((cl) => (
                                                <span key={cl.id} className="me-2">
                                                    {childName(cl) || i18n.t('Option')} × {cl.quantity}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    {optionChildren.map((cl) => (
                                        <div key={cl.id}>
                                            + {childName(cl) || i18n.t('Option')} × {cl.quantity}
                                            {normalizeErrorList(cl.errors).length > 0 && (
                                                <ul className="text-danger mb-0 mt-1 ps-3" style={{ fontSize: '0.75rem', listStylePosition: 'outside' }}>
                                                    {normalizeErrorList(cl.errors).map((msg, idx) => (
                                                        <li key={`${cl.id}-err-${idx}`}>{msg}</li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="d-flex align-items-end justify-content-between gap-3 flex-wrap mt-3">
                                <QuantityStepper
                                    value={stepperQty}
                                    min={1}
                                    step={1}
                                    disabled={lineLoading}
                                    disabledDecrement={lineLoading || stepperQty <= 1}
                                    disabledIncrement={lineLoading}
                                    onChange={setStepperQty}
                                    inputAriaLabel={i18n.t('Quantity')}
                                    decrementAriaLabel={i18n.t('Decrease quantity')}
                                    incrementAriaLabel={i18n.t('Increase quantity')}
                                    minusIcon={<Icon name="ICON_MINUS" />}
                                    plusIcon={<Icon name="ICON_PLUS" />}
                                    className="cart-line-qty-stepper"
                                />
                                <div className="text-end ms-auto">
                                    <div className="small text-muted">{i18n.t('Total')}</div>
                                    <div className="fw-semibold">{currencyFormatter(lineTotal)}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="row border-bottom py-3 align-items-center" key={line.id}>
                <div className="col-12 col-md-6">
                    <div className="d-flex align-items-start gap-3">
                        {primaryImage?.src ? (
                            hasProductUrl ? (
                                <a href={productUrl} className="d-inline-block flex-shrink-0">
                                    <img
                                        src={primaryImage.src}
                                        alt={primaryImage.alt}
                                        width={imageSize}
                                        height={imageSize}
                                        loading="lazy"
                                        className="rounded border bg-white"
                                        style={{ objectFit: 'contain' }}
                                    />
                                </a>
                            ) : (
                                <img
                                    src={primaryImage.src}
                                    alt={primaryImage.alt}
                                    width={imageSize}
                                    height={imageSize}
                                    loading="lazy"
                                    className="rounded border bg-white"
                                    style={{ objectFit: 'contain' }}
                                />
                            )
                        ) : (
                            <div
                                className="rounded border bg-light flex-shrink-0"
                                style={{ width: imageSize, height: imageSize }}
                                aria-hidden="true"
                            />
                        )}
                        <div className="flex-grow-1">
                            {hasProductUrl ? (
                                <a href={productUrl} className="text-decoration-none text-dark fw-semibold">
                                    {productObj.name || i18n.t('Product')}
                                </a>
                            ) : (
                                <div className="text-dark fw-semibold">
                                    {productObj.name || i18n.t('Product')}
                                </div>
                            )}
                            {lineErrorList}
                            <div className="small text-muted mt-1">
                                <PriceGroups
                                    groups={priceGroups}
                                    unitPrice={unitPrice * quantity}
                                    unitDiscountPrice={unitDiscountPrice * quantity}
                                    hasDiscount={hasDiscount}
                                />
                            </div>
                            {(componentChildren.length > 0 || optionChildren.length > 0) && (
                                <div className="small text-muted mt-1">
                                    {componentChildren.length > 0 && (
                                        <div className="mb-1">
                                            <span className="fw-semibold">{i18n.t('Includes')}: </span>
                                            {componentChildren.map((cl) => (
                                                <span key={cl.id} className="me-2">
                                                    {childName(cl) || i18n.t('Option')} × {cl.quantity}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    {optionChildren.map((cl) => (
                                        <div key={cl.id}>
                                            + {childName(cl) || i18n.t('Option')} × {cl.quantity}
                                            {normalizeErrorList(cl.errors).length > 0 && (
                                                <ul className="text-danger mb-0 mt-1 ps-3" style={{ fontSize: '0.75rem', listStylePosition: 'outside' }}>
                                                    {normalizeErrorList(cl.errors).map((msg, idx) => (
                                                        <li key={`${cl.id}-err-${idx}`}>{msg}</li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="col-6 col-md-2 d-flex align-items-center">
                    <QuantityStepper
                        value={stepperQty}
                        min={1}
                        step={1}
                        disabled={lineLoading}
                        disabledDecrement={lineLoading || stepperQty <= 1}
                        disabledIncrement={lineLoading}
                        onChange={setStepperQty}
                        inputAriaLabel={i18n.t('Quantity')}
                        decrementAriaLabel={i18n.t('Decrease quantity')}
                        incrementAriaLabel={i18n.t('Increase quantity')}
                        minusIcon={<Icon name="ICON_MINUS" />}
                        plusIcon={<Icon name="ICON_PLUS" />}
                        className="cart-line-qty-stepper"
                    />
                </div>
                <div className="col-4 col-md-2 text-end">
                    <div className="fw-semibold">{currencyFormatter(lineTotal)}</div>
                </div>
                <div className="col-2 col-md-2 text-end">
                    <button
                        type="button"
                        className="btn btn-link p-0"
                        onClick={() => updateQuantity(line.id, 0)}
                        disabled={lineLoading}
                        title={i18n.t('Remove')}
                        aria-label={i18n.t('Remove')}
                    >
                            <Icon name="ICON_TRASH" />
                    </button>
                </div>
            </div>
        );
    };

    if (isLoading && !cart) {
        return (
            <div className={containerClassName}>
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <h4 className="mb-0">{i18n.t('Shopping cart')}</h4>
                    <span className="badge text-bg-secondary">{i18n.t('Loading')}</span>
                </div>
                <CartOverviewSkeleton />
            </div>
        );
    }

    if (error) {
        return (
            <div className={containerClassName}>
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <h4 className="mb-0">{i18n.t('Shopping cart')}</h4>
                </div>
                <div className="card border-danger-subtle">
                    <div className="card-body py-5 text-center">
                        <div className="mb-3 text-danger" style={{ fontSize: 28, lineHeight: 1 }}>
                            <Icon name="ICON_WARNING" />
                        </div>
                        <h5 className="mb-2">{i18n.t('Could not load cart')}</h5>
                        <p className="text-muted mb-4">{i18n.t('Please check your connection and try again')}</p>
                        <a href="/" className="btn btn-outline-secondary">
                            {i18n.t('Continue shopping')}
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    const cartLines = cart?.cart_lines || [];
    const cartErrors = normalizeErrorList(cart?.errors);
    const hasLineIssues = cartLines.some((line) => {
        if (normalizeErrorList(line.errors).length > 0) return true;
        const children = line.child_lines || line.cart_lines || [];
        return children.some((cl) => normalizeErrorList(cl.errors).length > 0);
    });
    const hasCartIssues = cartErrors.length > 0 || hasLineIssues;
    const totalQuantity = cart?.total_quantity ?? cartLines.reduce((sum, line) => sum + (line.quantity || 0), 0);
    let totalPrice = 0;
    let totalDiscountPrice = 0;
    cartLines.forEach(line => {
        const qty = line.quantity || 1;
        const children = (line.child_lines || line.cart_lines || []).filter((child) => child.line_role !== 'component');
        const groups = linePriceGroups(line, qty, children);
        if (groups.length) {
            groups.forEach((row) => {
                totalDiscountPrice += row.amount;
            });
            totalPrice += money(line.price_in_vat) * qty;
            children.forEach((child) => {
                totalPrice += money(child.price_in_vat ?? child.disc_price_in_vat) * (child.quantity || 0);
            });
            return;
        }
        totalPrice += money(line.price_in_vat) * qty;
        totalDiscountPrice += money(line.disc_price_in_vat ?? line.price_in_vat) * qty;
    });
    const shippingEstimates = (cart?.shipping_estimates || [])
        .map((row) => ({ ...row, price: parseFloat(row.price) }))
        .filter((row) => Number.isFinite(row.price));
    const lowestShipping = shippingEstimates.length
        ? Math.min(...shippingEstimates.map((row) => row.price))
        : null;
    const subtotal = totalDiscountPrice;
    const estimatedTotal = lowestShipping === null ? subtotal : subtotal + lowestShipping;

    return (
        <div className={containerClassName}>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <h4 className="mb-0">{i18n.t('Shopping cart')}</h4>
                <div className="d-flex align-items-center gap-2">
                    <span className="badge text-bg-secondary">
                        {totalQuantity} {totalQuantity === 1 ? i18n.t('item') : i18n.t('items')}
                    </span>
                    {isDrawer && onClose ? (
                        <button
                            type="button"
                            className="btn btn-link text-dark p-0"
                            aria-label={i18n.t('Close')}
                            onClick={onClose}
                        >
                            <Icon name="ICON_X" />
                        </button>
                    ) : null}
                </div>
            </div>
            {cartLines.length === 0 ? (
                <div className="card">
                    <div className="card-body py-5 text-center">
                        <div className="mb-3 text-muted" style={{ fontSize: 28, lineHeight: 1 }}>
                            <Icon name="bi-cart3" />
                        </div>
                        <h5 className="mb-2">{i18n.t('Your cart is empty')}</h5>
                        <p className="text-muted mb-4">{i18n.t('Add some products to continue')}</p>
                        {isDrawer && onClose ? (
                            <button type="button" className="btn btn-primary" onClick={onClose}>
                                {i18n.t('Continue shopping')}
                            </button>
                        ) : (
                            <a href="/" className="btn btn-primary">
                                {i18n.t('Continue shopping')}
                            </a>
                        )}
                    </div>
                </div>
            ) : (
                <>
                    {hasCartIssues && (
                        <div className="alert alert-warning d-flex align-items-start gap-2 mb-3" role="alert">
                            <Icon name="ICON_WARNING" className="flex-shrink-0 mt-1" />
                            <div>
                                <div className="fw-semibold">{i18n.t('Action required before checkout')}</div>
                                {cartErrors.length > 0 ? (
                                    <ul className="small mb-0 ps-3">
                                        {cartErrors.map((msg, idx) => (
                                            <li key={`cart-err-${idx}`}>{msg}</li>
                                        ))}
                                    </ul>
                                ) : (
                                    <div className="small mb-0">
                                        {i18n.t(
                                            'One or more lines have errors (for example stock). Resolve them or adjust your order.',
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {lineError && (
                        <div className="alert alert-warning d-flex align-items-center flex-wrap gap-2" role="alert">
                            <div className="d-flex align-items-center">
                                <div className="me-2"><Icon name="ICON_WARNING" /></div>
                                <div>
                                    <div className="fw-semibold">{i18n.t('Could not update cart')}</div>
                                    <div className="small text-muted">{i18n.t('Your changes may not be saved yet')}</div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className={`row ${isDrawer ? 'g-3' : 'g-4'}`}>
                        <div className={isDrawer ? 'col-12' : 'col-12 col-lg-8'}>
                            <div className="card position-relative">
                                <div className="card-header bg-white d-flex align-items-center justify-content-between">
                                    <div className="text-muted small text-uppercase fw-bold mb-0">
                                        {i18n.t('Items')}
                                    </div>
                                    {lineLoading && (
                                        <div className="text-muted small d-flex align-items-center">
                                            <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
                                            <span>{i18n.t('Updating')}</span>
                                        </div>
                                    )}
                                </div>
                                {isOverlayUpdating && (
                                    <div
                                        className="position-absolute top-0 start-0 w-100 h-100"
                                        style={{ background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(1px)', zIndex: 1 }}
                                    >
                                        <div className="d-flex align-items-center justify-content-center h-100">
                                            <div className="text-muted small d-flex align-items-center">
                                                <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
                                                <span>{i18n.t('Updating')}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className="card-body py-0">
                                    {cartLines.map(line => (
                                        <CartLineRow key={line.id} line={line} />
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className={isDrawer ? 'col-12 position-sticky bottom-0 pb-1' : 'col-12 col-lg-4'}>
                            <div className="card">
                                <div className="card-header bg-white">
                                    <div className="text-muted small text-uppercase fw-bold mb-0">
                                        {i18n.t('Summary')}
                                    </div>
                                </div>
                                <div className="card-body">
                                    <div className="d-flex justify-content-between mb-2">
                                        <span className="text-muted">{i18n.t('Subtotal')}</span>
                                        <span className="fw-semibold">{currencyFormatter(subtotal)}</span>
                                    </div>
                                    {totalPrice !== totalDiscountPrice && (
                                        <div className="d-flex justify-content-between mb-2">
                                            <span className="text-muted">{i18n.t('Discount')}</span>
                                            <span className="text-success">-{currencyFormatter(totalPrice - totalDiscountPrice)}</span>
                                        </div>
                                    )}
                                    <hr />
                                    {shippingEstimates.length > 0 && (
                                        <>
                                            <div className="text-muted small text-uppercase fw-bold mb-2">
                                                {i18n.t('Estimate')}
                                            </div>
                                            {shippingEstimates.map((row) => (
                                                <div className="d-flex justify-content-between mb-2" key={row.id}>
                                                    <span className="text-muted">{row.name}</span>
                                                    <span className="fw-semibold">
                                                        {row.price === 0 ? i18n.t('Free') : currencyFormatter(row.price)}
                                                    </span>
                                                </div>
                                            ))}
                                        </>
                                    )}
                                    {shippingEstimates.length > 0 && (
                                        <p className="small text-muted mt-2 mb-0">
                                            {i18n.t('Shipping is an estimate, and can change based on region and method.')}
                                        </p>
                                    )}
                                    <hr />
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span className="fw-bold">
                                            {shippingEstimates.length > 1 ? i18n.t('Total from') : i18n.t('Total')}
                                        </span>
                                        <span className="fw-bold fs-5">{currencyFormatter(estimatedTotal)}</span>
                                    </div>
                                    <p className="small text-muted mt-2 mb-0">
                                        {shippingEstimates.length
                                            ? i18n.t('Including VAT.')
                                            : i18n.t('Including VAT, excluding shipping costs')}
                                    </p>
                                </div>
                                <div className="card-footer bg-white d-grid gap-2">
                                    {hasCartIssues ? (
                                        <button type="button" className="btn btn-primary" disabled>
                                            {i18n.t('Checkout')}
                                        </button>
                                    ) : lineLoading || isLoading ? (
                                        <a
                                            className={`btn btn-primary ${lineLoading ? 'disabled' : ''}`}
                                            aria-disabled={lineLoading ? 'true' : 'false'}
                                        >
                                            {lineLoading ? i18n.t('Updating') : isLoading ? i18n.t('Loading') : i18n.t('Checkout')}
                                        </a>
                                    ) : (
                                        <a href="/order/checkout/" className="btn btn-primary">
                                            {i18n.t('Checkout')}
                                        </a>
                                    )}

                                    {isDrawer && onClose ? (
                                        <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                                            {i18n.t('Continue shopping')}
                                        </button>
                                    ) : (
                                        <a href="/" className="btn btn-outline-secondary">
                                            {i18n.t('Continue shopping')}
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default CartOverview;
